import React from 'react';

export const WelcomePage: React.FC = () => {
  return (
    <div className="container py-12 max-w-4xl">
      <div className="prose prose-lg max-w-none">
        <h1 className="text-4xl font-bold mb-4">
          <strong>AI Business Operations Engine (Project Vision)</strong>
        </h1>
        <h3 className="text-2xl font-semibold text-gray-700 mb-8 italic">
          The Intelligent Digital Workforce for the US Small Business Economy
        </h3>
        <hr className="my-8 border-gray-300" />

        <h2 className="text-3xl font-bold mt-8 mb-4">
          <strong>1. Executive Summary</strong>
        </h2>
        <p className="mb-6 text-gray-700 leading-relaxed">
          <strong>The AI Business Operations Engine</strong> is a specialized automation platform designed to rescue US small and medium businesses (SMBs) from administrative burnout. By integrating voice, messaging, and CRM into a single AI-driven ecosystem, we provide business owners with a "Digital Chief of Staff" that captures leads, books appointments, and ensures no verbal or written agreement ever falls through the cracks.
        </p>
        <hr className="my-8 border-gray-300" />

        <h2 className="text-3xl font-bold mt-8 mb-4">
          <strong>2. The Problem: The High Cost of Being "Busy"</strong>
        </h2>
        <p className="mb-4 text-gray-700">
          In the US service economy (Plumbing, HVAC, Legal, MedSpas), being "in the field" means losing money at the office:
        </p>
        <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
          <li>
            <strong>The Response Gap:</strong> 62% of calls to SMBs go unanswered. If a lead isn't contacted within 5 minutes, the chance of conversion drops by 80%.
          </li>
          <li>
            <strong>Fragmented Chaos:</strong> Owners struggle to manage communications across SMS, Google Maps, Instagram, and Voice.
          </li>
          <li>
            <strong>Administrative Drain:</strong> Hiring a full-time office manager costs $4,000+/month—an expense many growing businesses can't justify.
          </li>
          <li>
            <strong>Invisible Reputation:</strong> Most businesses ignore reviews and comments, failing to realize these are now the primary data sources for the next generation of AI-driven search (ChatGPT, Gemini).
          </li>
        </ul>
        <hr className="my-8 border-gray-300" />

        <h2 className="text-3xl font-bold mt-8 mb-4">
          <strong>3. The Solution: Three Pillars of Intelligence</strong>
        </h2>

        <h3 className="text-2xl font-semibold mt-6 mb-4">
          <strong>A. Unified Omni-Channel Inbox & AI Agents</strong>
        </h3>
        <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
          <li>
            <strong>Contextual Draft-to-Approve:</strong> Our AI analyzes customer history and business policies to draft perfect responses. The owner receives a push notification, reviews the draft, and sends it with one tap. <strong>Control remains with the human.</strong>
          </li>
          <li>
            <strong>Smart Triage:</strong> Automated sorting of "high-intent" leads vs. general inquiries, ensuring owners only spend time on revenue-generating tasks.
          </li>
        </ul>

        <h3 className="text-2xl font-semibold mt-6 mb-4">
          <strong>B. Voice-to-Action Pipeline (RingCentral/Dialpad Sync)</strong>
        </h3>
        <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
          <li>
            <strong>Missed-Call Text-Back:</strong> When a call is missed, the AI instantly initiates an SMS conversation to qualify the lead and book the job.
          </li>
          <li>
            <strong>Post-Call Intelligence:</strong> Using advanced transcription, the AI extracts commitments (dates, prices, specific requests) and automatically creates tasks in the CRM. <strong>What was promised over the phone is never forgotten.</strong>
          </li>
        </ul>

        <h3 className="text-2xl font-semibold mt-6 mb-4">
          <strong>C. The "Action Layer" (Ecosystem Integration)</strong>
        </h3>
        <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
          <li>
            <strong>Autonomous Scheduling:</strong> Full two-way sync with Google Calendar, Outlook, and Acuity. The AI checks real-time availability and books slots without manual intervention.
          </li>
          <li>
            <strong>CRM Record Keeping:</strong> Seamless data write-back to HubSpot, Salesforce, or Zoho, maintaining a clean and updated customer database.
          </li>
        </ul>
        <hr className="my-8 border-gray-300" />

        <h2 className="text-3xl font-bold mt-8 mb-4">
          <strong>4. The Unfair Advantage: AI-SEO & Reputation Amplification</strong>
        </h2>
        <p className="mb-4 text-gray-700">
          We offer a feature most competitors overlook: <strong>AI Search Optimization.</strong>
        </p>
        <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
          <li>
            <strong>Strategic Engagement:</strong> Our AI responds to Google Reviews and Social Media comments by enriching them with specific keywords (service type, neighborhood, outcome).
          </li>
          <li>
            <strong>Long-Term Authority:</strong> This creates a data-rich footprint that trains future LLMs (like ChatGPT/Perplexity) to recommend our clients as the top authority in their local area.
          </li>
        </ul>
        <hr className="my-8 border-gray-300" />

        <h2 className="text-3xl font-bold mt-8 mb-4">
          <strong>5. Onboarding & Professional Services</strong>
        </h2>
        <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
          <li>
            <strong>The Knowledge Wizard:</strong> A simple 15-minute onboarding process where owners upload price lists and service areas. The AI builds its "Source of Truth" instantly.
          </li>
          <li>
            <strong>White-Glove Setup:</strong> For complex businesses, we offer professional AI-tuning and custom tool development to integrate with proprietary software.
          </li>
        </ul>
        <hr className="my-8 border-gray-300" />

        <h2 className="text-3xl font-bold mt-8 mb-4">
          <strong>6. Value Proposition for the US SMB</strong>
        </h2>
        <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
          <li>
            <strong>24/7 Availability:</strong> Capture leads while you sleep or while you're on a job site.
          </li>
          <li>
            <strong>Operational Efficiency:</strong> Replace the need for a $48k/year receptionist with a $4k/year intelligent platform.
          </li>
          <li>
            <strong>Revenue Growth:</strong> Stop the "lead leak" by responding to every inquiry across every platform in under 10 seconds.
          </li>
        </ul>
        <hr className="my-8 border-gray-300" />

        <h2 className="text-3xl font-bold mt-8 mb-4">
          <strong>7. Roadmap: MVP & Beyond</strong>
        </h2>
        <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
          <li>
            <strong>Q1: Core Messenger Hub</strong> (SMS/GMB/Instagram + Draft Mode + Calendar Sync).
          </li>
          <li>
            <strong>Q2: Voice Integration</strong> (RingCentral/Dialpad Missed-Call automation & CRM tasking).
          </li>
          <li>
            <strong>Q3: Reputation Engine</strong> (AI-SEO response automation & Advanced Knowledge Base Wizard).
          </li>
        </ul>
      </div>
    </div>
  );
};

